/**
 * Gráfico de tendencia, en SVG escrito a mano.
 *
 * Por qué sin librería de gráficos
 * --------------------------------
 * Este módulo ya trae MapLibre y deck.gl al dashboard (#43). Sumar Recharts o
 * Chart.js serían otros ~250 KB para dibujar dos áreas y unas barras — y las
 * dos librerías traen su propio formateo de fechas y números, que es
 * exactamente lo que acá no se puede usar: el formato es el del **tenant**, no
 * el del navegador, y sale de `fmt`.
 *
 * Con `series.ts` acotando la entrada a `MAX_CHART_POINTS` puntos, el SVG
 * resultante son ~90 nodos: menos que una fila de la tabla de usuarios.
 *
 * Accesibilidad: el `<svg>` es `role="img"` con un resumen en `aria-label`, y
 * debajo va la misma serie en una tabla desplegable. Un lector de pantalla no
 * lee un trazo, y un auditor quiere los números.
 */
import type { ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { areaPath, linePath, niceMax, seriesPoints, type Bucket } from './series';

const BOX = { width: 720, height: 150 };

export type SeriesKey = 'scans' | 'genuine' | 'suspicious' | 'units';

export interface TrendChartProps {
  buckets: readonly Bucket[];
  /** Qué campo de cada tramo se dibuja. */
  metric: SeriesKey;
  title: string;
  /** Resumen para lector de pantalla. */
  summary: string;
  tone: 'accent' | 'critical';
}

const STROKE: Record<TrendChartProps['tone'], string> = {
  accent: 'var(--color-accent)',
  critical: 'var(--color-critical)',
};

export function TrendChart({
  buckets,
  metric,
  title,
  summary,
  tone,
}: TrendChartProps): ReactNode {
  const { t, fmt } = useI18n();
  const values = buckets.map((bucket) => bucket[metric]);
  const max = niceMax(Math.max(0, ...values));
  const points = seriesPoints(values, max, BOX);
  const stroke = STROKE[tone];

  const first = buckets[0];
  const last = buckets[buckets.length - 1];
  const bandWidth = points.length > 1 ? BOX.width / (points.length - 1) : BOX.width;
  // Con todos los tramos en cero no hay línea que mostrar: ejes vacíos
  // parecen un gráfico que no cargó. Se dice qué pasa, como en el resto de
  // las pantallas (#172). `chartSeries` rellena el período con ceros, así que
  // "sin datos" y "todo en cero" son lo mismo acá.
  const vacio = values.every((value) => value === 0);

  return (
    <figure className="m-0">
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">{title}</span>
        <span className="text-xs tabular-nums text-ink-subtle">
          {vacio ? '' : t('analytics.chart.peak', { value: fmt.compact(max) })}
        </span>
      </figcaption>

      {vacio && (
        <div
          role="status"
          className="mt-2 flex h-[150px] items-center justify-center rounded border border-dashed border-border text-sm text-ink-muted"
        >
          {metric === 'scans' ? t('analytics.chart.empty') : t('analytics.chart.emptySuspicious')}
        </div>
      )}

      {!vacio && (
        <svg
          role="img"
          aria-label={summary}
          viewBox={`0 0 ${BOX.width} ${BOX.height}`}
          // `none` estira el trazo horizontalmente; `vector-effect` lo compensa
          // para que la línea no engorde en pantallas anchas.
          preserveAspectRatio="none"
          className="mt-2 h-[150px] w-full"
        >
          {[0.25, 0.5, 0.75].map((ratio) => (
            <line
              key={ratio}
              x1={0}
              x2={BOX.width}
              y1={BOX.height * ratio}
              y2={BOX.height * ratio}
              stroke="currentColor"
              className="text-border"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          <path d={areaPath(points, BOX)} fill={stroke} opacity={0.14} />
          <path
            d={linePath(points)}
            fill="none"
            stroke={stroke}
            strokeWidth={1.75}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* Una banda invisible por tramo, sólo para el tooltip nativo. Sin JS,
              sin estado y sin un listener por punto. */}
          {points.map((point, index) => {
            const bucket = buckets[index];
            if (bucket === undefined) return null;
            return (
              <rect
                key={bucket.from}
                x={Math.max(0, point.x - bandWidth / 2)}
                y={0}
                width={bandWidth}
                height={BOX.height}
                fill="transparent"
              >
                <title>
                  {`${fmt.date(bucket.from)}${bucket.days > 1 ? ` – ${fmt.date(bucket.to)}` : ''}: ${fmt.number(bucket[metric])}`}
                </title>
              </rect>
            );
          })}
        </svg>
      )}

      <div className="flex justify-between text-2xs text-ink-subtle">
        <span>{first === undefined ? '' : fmt.date(first.from)}</span>
        <span>{last === undefined ? '' : fmt.date(last.to)}</span>
      </div>
    </figure>
  );
}
